// src/hooks/customers/useCustomers.ts
import {useMemo} from "react";
import {
  useGetCustomersQuery,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useVerifyCustomerEmailMutation,
  useBulkDeleteCustomersMutation,
} from "../store/api/customersApi";
import type {UserFilterDto} from "../types/users";
import {useToast} from "./common/useToast";

export const useCustomers = (filters: UserFilterDto = {}) => {
  const {error} = useToast();

  // Query customers data
  const {
    data: customersData,
    isLoading,
    error: queryError,
    refetch,
  } = useGetCustomersQuery(filters);

  // Extract customers, pagination, and stats from response
  const customers = useMemo(() => customersData?.data || [], [customersData]);
  const pagination = useMemo(() => customersData?.pagination, [customersData]);
  const stats = useMemo(() => customersData?.stats, [customersData]);

  // Mutations

  const [updateCustomerMutation] = useUpdateCustomerMutation();
  const [deleteCustomerMutation] = useDeleteCustomerMutation();
  const [verifyEmailMutation] = useVerifyCustomerEmailMutation();
  const [bulkDeleteMutation] = useBulkDeleteCustomersMutation();

  // Handle update customer
  const handleUpdate = async (id: number, data: any) => {
    try {
      await updateCustomerMutation({id, data}).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to update customer");
      return false;
    }
  };

  // Handle delete customer
  const handleDelete = async (id: number) => {
    try {
      await deleteCustomerMutation(id).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to delete customer");
      return false;
    }
  };

  // Handle verify customer email
  const handleVerifyEmail = async (id: number) => {
    try {
      await verifyEmailMutation(id).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to verify customer email");
      return false;
    }
  };

  // Handle bulk delete customers
  const handleBulkDelete = async (ids: number[]) => {
    try {
      await bulkDeleteMutation(ids).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to delete customers");
      return false;
    }
  };

  // Show error toast if query fails
  if (queryError) {
    error("Error", "Failed to load customers data");
  }

  return {
    customers,
    pagination,
    stats,
    isLoading,
    queryError,
    refetch,
    handleUpdate,
    handleDelete,
    handleVerifyEmail,
    handleBulkDelete,
  };
};
